/**
 * fixedNodes.ts
 * 固定ノード（Start/End）の設定
 * これらのノードは削除できず、フローに必ず存在する（位置は移動可能）
 */
import type { NodeDataMap } from '../types';
import { getDefaultData } from '../nodeRegistry';
import type { FlowLayoutPreset } from './layoutPreset';

/** 固定ノードのID型（Start と End のみ） */
type FixedNodeId = 'start' | 'end' | 'input' | 'output';

/**
 * 固定ノードの設定型
 * id と type を同じキーから導出することで不一致を型レベルで防止
 */
type FixedNodeConfig<K extends FixedNodeId> = {
  id: K;
  type: K;
  position: { x: number; y: number };
  data: NodeDataMap[K];
  deletable: false;   // 削除不可
};

/**
 * 固定ノード設定を生成するヘルパー関数
 * キーから id/type を自動設定し、重複記述を排除
 */
const createFixedNode = <K extends FixedNodeId>(
  key: K,
  position: { x: number; y: number }
): FixedNodeConfig<K> => ({
  id: key,
  type: key,
  position,
  data: getDefaultData(key),
  deletable: false,
});

/**
 * 画面サイズプリセットごとの固定ノード位置
 */
const FIXED_NODE_POSITIONS = {
  mobile: {
    start: { x: -70, y: 130 },
    end: { x: 520, y: 130 },
    input: { x: -70, y: 130 },
    output: { x: 520, y: 130 },
  },
  tablet: {
    start: { x: -125, y: 140 },
    end: { x: 695, y: 140 },
    input: { x: -125, y: 140 },
    output: { x: 695, y: 140 },
  },
  desktop: {
    start: { x: -160, y: 150 },
    end: { x: 790, y: 150 },
    input: { x: -160, y: 150 },
    output: { x: 790, y: 150 },
  },
} as const satisfies Record<
  FlowLayoutPreset,
  Record<FixedNodeId, { x: number; y: number }>
>;

/** 固定ノードIDのセット（高速な判定用） */
const FIXED_NODE_ID_SET = new Set<FixedNodeId>(['start', 'end', 'input', 'output']);

/**
 * 指定されたIDが固定ノードかどうかを判定
 * 削除操作などで固定ノードを保護するために使用
 */
export const isFixedNodeId = (nodeId: string): boolean =>
  FIXED_NODE_ID_SET.has(nodeId as FixedNodeId);

/** 初期ノード配列を取得（FlowEditorの初期状態用） */
export const getInitialNodes = (preset: FlowLayoutPreset) => {
  const positions = FIXED_NODE_POSITIONS[preset];
  const fixedNodes = [
    createFixedNode('start', positions.start),
    createFixedNode('end', positions.end),
  ] satisfies [FixedNodeConfig<'start'>, FixedNodeConfig<'end'>];
  // useNodesState で扱いやすいよう readonly タプルを通常配列へ変換して返す
  return [...fixedNodes];
};

export const getInitialFunctionNodes = (preset: FlowLayoutPreset) => {
  const positions = FIXED_NODE_POSITIONS[preset];
  return [
    createFixedNode('input', positions.input),
    createFixedNode('output', positions.output),
  ];
};

